import React from "react";
import { Popup } from "../../../../UI/Popup/Popup";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import styles from "./MentorsListModal.module.css";

/**
 * The best-matched mentors popup, as the design draws it: every mentor in it is
 * available on the chosen slots (R-05-06), and the ops user picks ONE to assign
 * directly (R-05-10 — loop requests are out of scope, so there is no
 * multi-select). View profile opens the mentor's page in a new tab.
 *
 * @param {{ open: boolean,
 *           mentors: {id: number, name: string, tier: string, rating: number|null,
 *                     shift: string, activeBatches?: number|null,
 *                     conflictCount?: number|null, profileUrl?: string}[],
 *           selectedId: number|null,
 *           onSelect: (id: number) => void,
 *           onClose: () => void, onConfirm: () => void }} props
 */
const MentorsListModal = ({ open, mentors, selectedId, onSelect, onClose, onConfirm }) => {
  if (!open) return null;

  return (
    <Popup onClose={onClose} contentClassName={styles.popupShell} showCloseIcon={false}>
      <div className={styles.modal} role="dialog" aria-label="Best-matched mentors">
        <h3 className={styles.title}>We’ve fetched suggested mentors based on the best match. Select a mentor to assign.</h3>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkCell} aria-label="Select" />
                <th className={styles.nameCell}>Mentor</th>
                <th>Tier</th>
                <th>Rating</th>
                <th>Shift</th>
                <th>View profile</th>
              </tr>
            </thead>
            <tbody>
              {mentors?.map((mentor) => (
                <tr
                  key={mentor?.id}
                  className={styles.row}
                  onClick={() => onSelect?.(mentor?.id)}
                >
                  <td className={styles.checkCell}>
                    <input
                      type="radio"
                      name="best-matched-mentor"
                      className={styles.checkbox}
                      aria-label={`Select ${mentor?.name}`}
                      checked={selectedId === mentor?.id}
                      onChange={() => onSelect?.(mentor?.id)}
                    />
                  </td>
                  <td className={styles.nameCell}>
                    {mentor?.name}
                    {mentor?.conflictCount > 0 && (
                      <span className={styles.conflicts}>
                        {mentor?.conflictCount} calendar conflict
                        {mentor?.conflictCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </td>
                  <td>{mentor?.tier || "—"}</td>
                  <td>
                    <span className={styles.rating}>
                      {mentor?.rating ?? "Not rated"}
                      {mentor?.rating !== null && mentor?.rating !== undefined && (
                        <SvgIcons name="star_filled" width="16" height="16" />
                      )}
                    </span>
                  </td>
                  <td className={styles.shiftCell}>{mentor?.shift}</td>
                  <td className={styles.profileCell}>
                    {mentor?.profileUrl && (
                      <a
                        href={mentor?.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.profileLink}
                        title={`View ${mentor?.name}'s profile`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <SvgIcons name="external_link" width="16" height="16" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.close} onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className={styles.confirm}
            disabled={selectedId === null || selectedId === undefined}
            onClick={onConfirm}
          >
            Confirm mentor
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default MentorsListModal;
